import { useState, type ReactNode } from "react";
import { type TextInputProps } from "react-native";
import type { LucideIcon } from "lucide-react-native";
import { useLocale } from "@src/packages/i18n";
import { MoonFormField } from "./MoonFormField";
import { MoonValueInput } from "./MoonValueInput";
import {
  cleanNumericInput,
  formatForDisplay,
  parseBuffer,
} from "./numericBuffer";

/**
 * Numeric text input: a numeric keyboard and integer or decimal parsing
 * over MoonValueInput, the box every value with a unit shares.
 *
 * Replaces the hand-rolled "TextInput with keyboardType numeric inside a
 * bordered View" pattern that the audit (task 2.2) flagged in 6+ modals.
 *
 * Numeric semantics:
 *   - `value` is always the *parsed number* (or null when the field is
 *     empty). Callers don't deal with strings.
 *   - Internally tracks a string buffer to allow intermediate states like
 *     "12." while the user types a decimal.
 *   - On blur, the string is re-parsed and `onChange` fires with the
 *     clamped value (min / max applied).
 *   - Empty string → onChange(null) so a cleared input reads as "no value"
 *     rather than 0.
 *
 * State coverage (Phase 1.0 matrix):
 *   - idle / focused / error / disabled handled by MoonFieldInput +
 *     MoonFormField composition.
 *   - loading flips MoonFormField's child to a skeleton.
 */
type Props = {
  label: string;
  /** Optional icon rendered beside the label (forwarded to MoonFormField). */
  labelIcon?: LucideIcon;
  /** Supporting copy rendered directly below the label and above the input. */
  labelHint?: string;
  /** Parsed value. `null` means the field is empty. */
  value: number | null;
  /** Fires with the parsed value (clamped to min/max) or null. */
  onChange: (next: number | null) => void;
  placeholder?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
  /** Number of decimal places allowed. 0 = integer-only (default). */
  decimals?: number;
  min?: number;
  max?: number;
  /** Optional prefix rendered inside the field (e.g. "$"). */
  prefix?: string;
  /** Optional suffix rendered inside the field (e.g. "kg"). */
  suffix?: string;
  /**
   * A control rendered inside the field after the value, such as a
   * `MoonUnitToggle`.
   */
  trailing?: ReactNode;
  /** Pass-through TextInput props for fine-grained control. */
  textInputProps?: Omit<
    TextInputProps,
    | "value"
    | "onChangeText"
    | "onFocus"
    | "onBlur"
    | "editable"
    | "keyboardType"
    | "placeholder"
    | "placeholderTextColor"
    | "style"
  >;
  testID?: string;
};

function clamp(value: number, min?: number, max?: number): number {
  if (min !== undefined && value < min) return min;
  if (max !== undefined && value > max) return max;
  return value;
}

export function MoonNumericField({
  label,
  labelIcon,
  labelHint,
  value,
  onChange,
  placeholder,
  error,
  hint,
  required,
  disabled,
  loading,
  decimals = 0,
  min,
  max,
  prefix,
  suffix,
  trailing,
  textInputProps,
  testID,
}: Props) {
  const [focused, setFocused] = useState(false);
  // French writes twelve and a half as "12,5", and its keyboards put a comma
  // on the decimal key; English uses the comma to group thousands.
  const decimalComma = useLocale().locale === "fr";
  // Local buffer so the user can type intermediate states like "12." that
  // wouldn't survive a round-trip through parseFloat.
  const [buffer, setBuffer] = useState<string>(() =>
    formatForDisplay(value, decimals, decimalComma),
  );

  // Sync buffer when parent value changes from outside (e.g. form reset).
  // Only when not focused so we don't yank the cursor mid-typing.
  if (!focused && parseBuffer(buffer, decimals) !== value) {
    setBuffer(formatForDisplay(value, decimals, decimalComma));
  }

  const handleChangeText = (text: string) => {
    const cleaned = cleanNumericInput(text, decimals, decimalComma);
    setBuffer(cleaned);

    const parsed = parseBuffer(cleaned, decimals);
    onChange(parsed === null ? null : clamp(parsed, min, max));
  };

  const handleBlur = () => {
    setFocused(false);
    // Re-format on blur so the buffer mirrors the canonical numeric form.
    const parsed = parseBuffer(buffer, decimals);
    const next = parsed === null ? null : clamp(parsed, min, max);
    setBuffer(formatForDisplay(next, decimals, decimalComma));
    if (next !== value) onChange(next);
  };

  return (
    <MoonFormField
      label={label}
      labelIcon={labelIcon}
      labelHint={labelHint}
      error={error}
      hint={hint}
      required={required}
      disabled={disabled}
      loading={loading}
      testID={testID}
    >
      <MoonValueInput
        value={buffer}
        onChangeText={handleChangeText}
        onFocus={() => setFocused(true)}
        onBlur={handleBlur}
        hasError={!!error}
        disabled={disabled}
        keyboardType={decimals > 0 ? "decimal-pad" : "number-pad"}
        placeholder={placeholder}
        prefix={prefix}
        suffix={suffix}
        trailing={trailing}
        textInputProps={textInputProps}
      />
    </MoonFormField>
  );
}
